import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Textarea, Label, Skeleton, ErrorState, Alert } from '@/components/ui'
import { Upload, X } from 'lucide-react'
import { api, type OrgInfo } from '@/api/client'

export function GeneralSettingsPage() {
  const { orgSlug } = useParams()
  const [org, setOrg] = useState<OrgInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [isUploadingBanner, setIsUploadingBanner] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const bannerInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const fetchOrg = async () => {
      if (!orgSlug) return
      try {
        setError(null)
        const data = await api.orgs.get(orgSlug)
        setOrg(data.org)
      } catch (err) {
        console.error('Failed to fetch org:', err)
        setError(err instanceof Error ? err.message : 'Failed to load organization')
      } finally {
        setIsLoading(false)
      }
    }
    fetchOrg()
  }, [orgSlug])

  const handleSave = async () => {
    if (!orgSlug || !org) return
    setIsSaving(true)
    try {
      await api.orgs.update(orgSlug, {
        name: org.name,
        description: org.description ?? undefined,
        website_url: org.website_url ?? undefined,
      })
    } catch (err) {
      console.error('Failed to save:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !orgSlug) return
    setUploadError(null)
    setIsUploadingLogo(true)
    try {
      const result = await api.orgs.uploadLogo(orgSlug, file)
      setOrg(prev => prev ? { ...prev, logo_url: result.url } : prev)
    } catch (err) {
      console.error('Failed to upload logo:', err)
      setUploadError(err instanceof Error ? err.message : 'Failed to upload logo')
    } finally {
      setIsUploadingLogo(false)
      if (logoInputRef.current) logoInputRef.current.value = ''
    }
  }

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !orgSlug) return
    setUploadError(null)
    setIsUploadingBanner(true)
    try {
      const result = await api.orgs.uploadBanner(orgSlug, file)
      setOrg(prev => prev ? { ...prev, banner_logo_url: result.url } : prev)
    } catch (err) {
      console.error('Failed to upload banner:', err)
      setUploadError(err instanceof Error ? err.message : 'Failed to upload banner')
    } finally {
      setIsUploadingBanner(false)
      if (bannerInputRef.current) bannerInputRef.current.value = ''
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-2xl">
        <Card>
          <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-20 w-20 rounded-lg" />
              <Skeleton className="h-9 w-32" />
            </div>
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error || !org) {
    return <ErrorState title="Failed to load organization" description={error || 'Organization not found'} action={<Button onClick={() => window.location.reload()}>Try Again</Button>} />
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {uploadError && (
        <Alert variant="error" onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      <Card>
        <CardHeader><CardTitle>Organization Branding</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          {/* Square Logo */}
          <div className="space-y-2">
            <Label>Logo (Square)</Label>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-lg bg-[var(--bg-elevated-2)] flex items-center justify-center overflow-hidden">
                {org.logo_url ? (
                  <img src={org.logo_url} alt={org.name} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold text-[var(--text-muted)]">{org.name?.[0] || '?'}</span>
                )}
              </div>
              <div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  variant="secondary"
                  onClick={() => logoInputRef.current?.click()}
                  loading={isUploadingLogo}
                  disabled={isUploadingLogo}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {isUploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-[var(--text-muted)]">Recommended: 256x256px, PNG, JPEG, SVG, or WebP. Max 2MB.</p>
          </div>

          {/* Banner Logo */}
          <div className="space-y-2">
            <Label>Banner Logo (Wide)</Label>
            <div className="space-y-3">
              <div className="w-full h-24 rounded-lg bg-[var(--bg-elevated-2)] flex items-center justify-center overflow-hidden">
                {org.banner_logo_url ? (
                  <img src={org.banner_logo_url} alt={`${org.name} banner`} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-sm text-[var(--text-muted)]">No banner uploaded</span>
                )}
              </div>
              <div>
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleBannerUpload}
                  className="hidden"
                />
                <Button
                  variant="secondary"
                  onClick={() => bannerInputRef.current?.click()}
                  loading={isUploadingBanner}
                  disabled={isUploadingBanner}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {isUploadingBanner ? 'Uploading...' : 'Upload Banner'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-[var(--text-muted)]">Recommended: 1200x300px, PNG, JPEG, or WebP. Max 5MB.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Organization Info</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Input label="Organization Name" value={org.name} onChange={(e) => setOrg({ ...org, name: e.target.value })} />
          <Input label="Slug" value={org.slug} disabled hint={`Used in URLs: console.u.software/orgs/${org.slug}`} />
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" value={org.description || ''} onChange={(e) => setOrg({ ...org, description: e.target.value })} rows={3} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Contact Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Input label="Website" placeholder="https://example.com" value={org.website_url || ''} onChange={(e) => setOrg({ ...org, website_url: e.target.value })} />
          <Input label="Support Email" type="email" placeholder="support@example.com" value={org.support_email || ''} onChange={(e) => setOrg({ ...org, support_email: e.target.value })} hint="Shown to users in consent screens and error pages" />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} loading={isSaving}>Save Changes</Button>
      </div>
    </div>
  )
}
